import React, { FC, useMemo, useState } from 'react';
import { Button } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import useCallbackRef from 'hooks/useCallbackRef';
import useHeightAnimation from 'hooks/useHeightAnimation';

import './ExpandableText.styles.scss';

const TEXT_LENGTH_LIMIT = 180;

type ExpandableTextProps = {
  className?: string;
  content: string;
  lengthLimit?: number;
};

const ExpandableText: FC<ExpandableTextProps> = ({
  className,
  content,
  lengthLimit = TEXT_LENGTH_LIMIT,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [container, containerRef] = useCallbackRef<HTMLElement>();

  const classes = classNames('expandable-text', className);
  const { t } = useTranslation();

  useHeightAnimation({
    element: container,
  });

  const trimmedContent = useMemo(() => content.trim(), [content]);

  const hasShowMore = useMemo(
    () => trimmedContent.length > lengthLimit,
    [lengthLimit, trimmedContent.length],
  );

  const croppedContent = useMemo(
    () => trimmedContent.substring(0, lengthLimit),
    [lengthLimit, trimmedContent],
  );

  return (
    <div className={classes}>
      <p ref={containerRef}>
        {isOpen
          ? trimmedContent
          : `${croppedContent}${hasShowMore ? '...' : ''}`}
      </p>

      {hasShowMore && (
        <Button variant="link" onClick={() => setIsOpen((old) => !old)}>
          {t(isOpen ? 'showLess' : 'showMore')}
        </Button>
      )}
    </div>
  );
};

export default ExpandableText;
